import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Plus as PlusIcon } from "@actinc/dls/icons";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useMutation } from "urql";

import { getRHFFieldOptionLabel } from "../../../services/formUtils";
import {
  useDraggableInPortal,
  useGetMetadataConfigurationOptions,
  useMemoCompare,
  useStore,
} from "../../../services/hooks";
import { getState } from "../../../services/sessionState";
import {
  getOptionsByProgram,
  getPreviewTypes,
  getSelectOptionsFromMetadata,
  getTaskPermissions,
  getUserOptions,
  onDragEnd,
} from "../../../services/utils.js";
import { UserContext } from "../../../userProvider";
import { UsersContext } from "../../../usersProvider";
import { createItemSetMutation } from "../../BulkProcesses/CreateContent";

import { AddItemDialog } from "./AddItemDialog";

import { PreviewContent } from "../../../components/";
import {
  READY_COMPONENT_TYPE,
  useReadyContext,
} from "../../../providers/ReadyProvider.js";

const ItemSetSummaryCard = ({
  activeTasks,
  allPrograms,
  metadataConfiguration,
  readonly,
  userTasks,
}) => {
  const addItemButtonRef = useRef();

  const renderDraggable = useDraggableInPortal();
  const { control, getValues } = useFormContext();

  const users = useContext(UsersContext);
  const user = useContext(UserContext);

  const { append, fields, move } = useFieldArray({
    control,
    keyName: "fieldArrayID",
    name: `passage.items`,
  });

  const [format] = useGetMetadataConfigurationOptions(
    metadataConfiguration,
    allPrograms,
    "item",
    ["format"],
  );

  const [loading, setLoading] = useState(false);
  const [selectedItemsVersions, setItemsVersionsSelected] = useState([]);

  const [passage, passageStore] = useStore("passage");

  const [mutateItemResponse, mutateItem] = useMutation(createItemSetMutation);
  const [addItemDialogProps, setAddItemDialogProps] = useState({ open: false });
  const { markReady } = useReadyContext();

  useEffect(() => {
    markReady([], READY_COMPONENT_TYPE.ITEM);
  }, []);

  const permissions = useMemo(
    () => getTaskPermissions(activeTasks.passage),
    [activeTasks.passage],
  );

  const itemsForPreview = useMemo(() => {
    return selectedItemsVersions.slice().sort((a, b) => {
      const indexA = fields.findIndex((field) => field.id === a.id);
      const indexB = fields.findIndex((field) => field.id === b.id);
      return indexA - indexB;
    });
  }, [selectedItemsVersions, fields]);

  const programs = allPrograms
    ?.filter(({ _actions }) => _actions.includes("createContent"))
    .map(({ configurationID, id: value, name: label }) => {
      return {
        configurationID,
        label,
        value,
      };
    });

  const program = passage?.program?.id;

  const createOptions = (availableOptions) => {
    if (!program) return;
    const id = programs.find((p) => p?.value === program)?.configurationID;
    const options = availableOptions
      .filter((option) => option?.configurations.includes(id))
      .map((obj) => ({ label: obj?.stringValue, value: obj?.stringValue }));
    return options;
  };

  const developmentStateOptions = getSelectOptionsFromMetadata(
    metadataConfiguration,
  )(programs)("passage")("developmentState");
  const developmentStates = createOptions(developmentStateOptions);
  const itemWriterOptions = getUserOptions(users)({
    activeOnly: true,
    programs: [program],
    roles: ["Item Writer"],
  });

  const itemTypeOptions = getSelectOptionsFromMetadata(metadataConfiguration)(
    programs,
  )("item")("type");

  if (!passage || !getValues("passage.id")) return null;

  const itemFormats = getOptionsByProgram(allPrograms)(program)(format);

  const contentSpecialistName = users.find(
    (element) => element?.id === passage?.contentSpecialist,
  )?.name;

  const createItem = async (formData) => {
    setLoading(true);
    try {
      const itemData = {
        data: {
          program: passage?.program?.id,
          contentArea: passage?.contentArea,
          contentSpecialist: {
            id: passage?.contentSpecialist,
            name: contentSpecialistName,
          },
        },
        passageID: passage?.id,
        passageName: null,
        itemSets: [
          {
            item: {
              format: formData.format,
              itemWriter: formData.itemWriter
                ? {
                    id: formData.itemWriter,
                    name: getRHFFieldOptionLabel(itemWriterOptions)(
                      formData.itemWriter,
                    ),
                  }
                : null,
              language: passage?.language,
            },
            numberToCreate: 1,
          },
        ],
      };
      const response = await mutateItem(itemData);
      const createdItem = response?.data?.createItemSet?.items?.[0];
      if (!createdItem) {
        return;
      }
      const newItemData = {
        depthOfKnowledge: null,
        description: "",
        developmentState: formData.developmentState,
        id: createdItem.id,
        key: "",
        name: createdItem.name,
        type: null,
        wordCount: null,
        pretestGroup: null,
      };
      append(newItemData);

      if (passageStore?.save) {
        const currentPassage = getValues("passage") || {};
        const updatedPassage = {
          ...currentPassage,
        };
        await passageStore.save(users)(allPrograms)(null)({ activeTasks })(
          false,
        )(null)({ shouldIncrementItemCount: true })(updatedPassage);
      }
    } finally {
      setLoading(false);
    }
  };

  const isSelected = (id) =>
    selectedItemsVersions.some((item) => {
      return item.id === id;
    });

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelectedItemVersion = fields
        .filter(
          (item) =>
            !permissions.includes("externalReview") ||
            userTasks.some((task) => task.item?.id === item.id),
        )
        .map((item) => {
          return { id: item.id };
        });
      setItemsVersionsSelected(newSelectedItemVersion);
    } else {
      setItemsVersionsSelected([]);
    }
  };

  const itemCheckboxClick = (event) => {
    let newSelected;
    if (isSelected(event.target.id)) {
      newSelected = selectedItemsVersions.filter((item) => {
        return item.id !== event.target.id;
      });
    } else {
      newSelected = [
        ...selectedItemsVersions,
        {
          id: event.target.id,
          versionID: event.target.versionID,
        },
      ];
    }
    setItemsVersionsSelected(newSelected);
  };

  const overflowStyling = {
    maxWidth: "10em",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "end",
          marginY: 1,
        }}
      >
        <PreviewContent
          objects={itemsForPreview}
          objectType="Item"
          previewTypes={getPreviewTypes(permissions)}
        />
        {!readonly && !permissions.includes("readonly") && (
          <Button
            onClick={() => {
              setAddItemDialogProps({
                defaultValues: {
                  contentArea: passage.contentArea,
                  contentSpecialist: contentSpecialistName,
                  developmentState: "Content Review 2",
                  format: "Selected Response",
                  itemWriter: passage.itemWriter,
                },
                developmentStates,
                itemFormats,
                itemWriterOptions,
                metadataConfiguration,
                onClose: (formData) => {
                  setAddItemDialogProps({ open: false });
                  if (!formData) return;
                  createItem(formData);
                },
                open: true,
                program: program,
                user: user,
              });
            }}
            color="primary"
            variant="outlined"
            startIcon={<PlusIcon />}
            size="small"
            align="right"
          >
            Add Item
          </Button>
        )}
      </Box>
      {!fields.length && (
        <Box>
          <Typography align="center" display="inline" variant="h6">
            There are no items associated with this item set.
          </Typography>
        </Box>
      )}
      {!!fields.length && (
        <TableContainer
          component={Paper}
          sx={{
            height: addItemButtonRef?.current
              ? `calc(100% - ${
                  addItemButtonRef.current.getBoundingClientRect().height + 16
                }px)`
              : undefined,
          }}
        >
          <Table aria-label="Items in the item set" size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    onChange={handleSelectAllClick}
                    inputProps={{
                      "aria-labelledby": "Select all Items",
                    }}
                    title="Select all Items"
                  />
                </TableCell>
                <TableCell align="right">#</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Item Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Type</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Dev State</TableCell>
                {!permissions.includes("externalReview") && (
                  <TableCell>Key</TableCell>
                )}
                <TableCell>DOK</TableCell>
                {!permissions.includes("externalReview") && (
                  <TableCell>Standards</TableCell>
                )}
                {!permissions.includes("externalReview") && (
                  <TableCell>Pretest Group</TableCell>
                )}
                {!permissions.includes("externalReview") && (
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    Word Count
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <DragDropContext onDragEnd={onDragEnd(move)}>
              <Droppable direction="vertical" droppableId={`passage.items`}>
                {(droppableProvided) => (
                  <TableBody
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                  >
                    {fields.map((item, index) => {
                      const isItemSelected = isSelected(item.id);
                      return (
                        <Draggable
                          draggableId={item.fieldArrayID}
                          index={index}
                          isDragDisabled={
                            readonly || permissions.includes("externalReview")
                          }
                          key={item.fieldArrayID}
                        >
                          {renderDraggable((draggableProvided) => (
                            <TableRow
                              aria-label={`draggable table row`}
                              ref={draggableProvided.innerRef}
                              sx={{
                                "&:active": {
                                  cursor: readonly ? "inherit" : "grabbing",
                                },
                                "&:hover": {
                                  cursor: readonly ? "inherit" : "grab",
                                },
                              }}
                              {...draggableProvided.draggableProps}
                              {...draggableProvided.dragHandleProps}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  color="primary"
                                  id={item.id}
                                  checked={isItemSelected}
                                  disabled={
                                    permissions.includes("externalReview") &&
                                    !userTasks.some(
                                      (task) => task.item?.id === item.id,
                                    )
                                  }
                                  onClick={itemCheckboxClick}
                                  inputProps={{
                                    "aria-labelledby": item.name,
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ width: "42px" }}>
                                {index + 1}
                              </TableCell>
                              <TableCell sx={{ ...overflowStyling }}>
                                {item.name}
                              </TableCell>
                              <TableCell sx={{ ...overflowStyling }}>
                                {item.description}
                              </TableCell>
                              <TableCell
                                sx={{ ...overflowStyling, width: "2em" }}
                              >
                                {
                                  itemTypeOptions.find((types) => {
                                    return types.stringValue === item.type;
                                  })?.abbrev
                                }
                              </TableCell>
                              <TableCell
                                sx={{ ...overflowStyling, width: "4em" }}
                              >
                                {
                                  developmentStateOptions.find((types) => {
                                    return (
                                      types.stringValue ===
                                      item.developmentState
                                    );
                                  })?.abbrev
                                }
                              </TableCell>
                              {!permissions.includes("externalReview") && (
                                <TableCell
                                  sx={{ ...overflowStyling, width: "2em" }}
                                >
                                  {item.key}
                                </TableCell>
                              )}
                              <TableCell
                                sx={{ ...overflowStyling, width: "2em" }}
                              >
                                {item.depthOfKnowledge}
                              </TableCell>
                              {!permissions.includes("externalReview") && (
                                <TableCell>
                                  {item.standards
                                    ?.filter(({ order }) => order === "primary")
                                    .map(
                                      (standard) => standard.humanCodingScheme,
                                    )
                                    .join(",") || ""}
                                </TableCell>
                              )}
                              {!permissions.includes("externalReview") && (
                                <TableCell>{item.pretestGroup}</TableCell>
                              )}
                              {!permissions.includes("externalReview") && (
                                <TableCell
                                  sx={{ ...overflowStyling, width: "4em" }}
                                  align="right"
                                >
                                  {item.wordCount}
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </Draggable>
                      );
                    })}
                    {droppableProvided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
          </Table>
        </TableContainer>
      )}
      {addItemDialogProps.open && <AddItemDialog {...addItemDialogProps} />}
    </Box>
  );
};

export { ItemSetSummaryCard };
